import upperFirst from 'lodash-es/upperFirst.js';
import { t } from '@lingui/core/macro';
import { SupportTaskStatus, SupportTaskKind } from '@duke-hq/svc-api-db/client';
import { Phone, MessageSquare, Mail, MessageCircle, Bell, LucideIcon } from 'lucide-react';

export function formatTaskKind(kind: SupportTaskKind): string {
  return upperFirst(kind.replace(/_/g, ' ').toLowerCase());
}

function formatTaskTitle(kind: SupportTaskKind, title?: string | null): string | null {
  if (title == null || title === '' || title === undefined) {
    return null;
  }

  if (
    kind === SupportTaskKind.NYB_SMS ||
    kind === SupportTaskKind.NYB_EMAIL ||
    kind === SupportTaskKind.NYB_CALL
  ) {
    return t`NYB Day ${title}`;
  }

  if (
    kind === SupportTaskKind.WITHDRAWAL_WARNING_STUDENT_CALL ||
    kind === SupportTaskKind.WITHDRAWAL_WARNING_EC_EMAIL ||
    kind === SupportTaskKind.WITHDRAWAL_WARNING_STUDENT_EMAIL ||
    kind === SupportTaskKind.NYB_WITHDRAWAL_WARNING_EC_EMAIL
  ) {
    return t`Withdrawal Warning Day ${title}`;
  }

  if (kind === SupportTaskKind.WITHDRAW_STUDENT_ENROLMENT) {
    return t`Auto-withdraw Day ${title}`;
  }

  if (kind === SupportTaskKind.REVISION_REMINDER_SMS) {
    return t`${title} units in revision`;
  }

  // For UNIT_RELEASED_SMS, the title is already formatted (e.g., "Unit Released: Unit Title")
  if (kind === SupportTaskKind.UNIT_RELEASED_SMS) {
    return title;
  }

  // For WELCOME_CALL and other tasks, return null to use the kind-specific formatting below
  return null;
}

export function formatTaskType(kind: SupportTaskKind, title?: string | null): string {
  const formattedTitle = formatTaskTitle(kind, title);
  if (formattedTitle !== null) {
    return formattedTitle;
  }

  if (kind === SupportTaskKind.WELCOME_CALL) {
    return t`Welcome Call`;
  }
  if (kind === SupportTaskKind.WELCOME_EMAIL) {
    return t`Welcome Email`;
  }
  if (kind === SupportTaskKind.COMPLETION_EMAIL) {
    return t`Completion Email`;
  }
  if (kind === SupportTaskKind.USI_NOT_VERIFIED_EMAIL) {
    return t`USI Not Verified`;
  }
  if (kind === SupportTaskKind.NON_VOC_COMPLETION_EC_EMAIL) {
    return t`Non-Vocational Completion (EC)`;
  }
  if (kind === SupportTaskKind.NON_VOC_COMPLETION_STUDENT_EMAIL) {
    return t`Non-Vocational Completion (Student)`;
  }
  if (kind === SupportTaskKind.NON_VOC_REENGAGEMENT_EMAIL) {
    return t`Re-engagement (Student)`;
  }
  if (kind === SupportTaskKind.MISSING_PHONE_EC_EMAIL) {
    return t`Missing Phone (EC)`;
  }
  if (kind === SupportTaskKind.NYB_SMS) {
    return t`NYB SMS`;
  }
  if (kind === SupportTaskKind.NYB_EMAIL) {
    return t`NYB Email`;
  }
  if (kind === SupportTaskKind.NYB_CALL) {
    return t`NYB Call`;
  }
  if (kind === SupportTaskKind.UNIT_RELEASED_SMS) {
    return t`Unit Released`;
  }
  if (kind === SupportTaskKind.REVISION_REMINDER_SMS) {
    return t`Revision Reminder`;
  }
  if (kind === SupportTaskKind.WITHDRAWAL_WARNING_STUDENT_CALL) {
    return t`Withdrawal Warning`;
  }
  if (
    kind === SupportTaskKind.WITHDRAWAL_WARNING_EC_EMAIL ||
    kind === SupportTaskKind.NYB_WITHDRAWAL_WARNING_EC_EMAIL
  ) {
    return t`Withdrawal Warning EC`;
  }
  if (kind === SupportTaskKind.WITHDRAWAL_WARNING_STUDENT_EMAIL) {
    return t`Withdrawal Warning (Student)`;
  }
  if (kind === SupportTaskKind.WITHDRAW_STUDENT_ENROLMENT) {
    return t`Withdraw Enrolment`;
  }
  if (kind === SupportTaskKind.REENROLMENT_REVIEW) {
    return t`Re-enrolment Review`;
  }
  if (kind === SupportTaskKind.SUPERSEDED_COURSE_TRANSFER) {
    return t`Superseded Course Transfer`;
  }
  if (kind === SupportTaskKind.CREDIT_TRANSFER_REVIEW) {
    return t`Credit Transfer Review`;
  }
  if (kind === SupportTaskKind.CREDIT_TRANSFER_FOLLOW_UP) {
    return t`Credit Transfer Follow-up`;
  }

  return formatTaskKind(kind);
}

export function formatTaskStatus(status: SupportTaskStatus): string {
  switch (status) {
    case SupportTaskStatus.COMPLETED:
      return t`Done`;
    case SupportTaskStatus.PENDING:
      return t`Pending`;
    case SupportTaskStatus.IN_PROGRESS:
      return t`In Progress`;
    case SupportTaskStatus.FAILED:
      return t`Failed`;
    case SupportTaskStatus.CANCELLED:
      return t`Cancelled`;
    case SupportTaskStatus.SKIPPED:
      return t`Skipped`;
    default:
      return status;
  }
}

export function getTaskStatusDisplay(status: SupportTaskStatus): {
  colorScheme: string;
  variant: string;
} {
  switch (status) {
    case SupportTaskStatus.COMPLETED:
      return { colorScheme: 'green', variant: 'solid' };
    case SupportTaskStatus.PENDING:
      return { colorScheme: 'gray', variant: 'outline' };
    case SupportTaskStatus.IN_PROGRESS:
      return { colorScheme: 'blue', variant: 'subtle' };
    case SupportTaskStatus.FAILED:
      return { colorScheme: 'red', variant: 'solid' };
    case SupportTaskStatus.CANCELLED:
      return { colorScheme: 'gray', variant: 'solid' };
    case SupportTaskStatus.SKIPPED:
      return { colorScheme: 'orange', variant: 'outline' };
    default:
      return { colorScheme: 'gray', variant: 'outline' };
  }
}

export function getTaskContactIcon(kind: SupportTaskKind): LucideIcon {
  const kindStr = kind.toString();
  if (kindStr.includes('CALL')) {
    return Phone;
  }
  if (kindStr.includes('SMS')) {
    return MessageSquare;
  }
  if (kindStr.includes('EMAIL')) {
    return Mail;
  }
  if (kindStr.includes('CHAT')) {
    return MessageCircle;
  }
  return Bell;
}

export function getContactTypeLabel(kind: SupportTaskKind): string {
  const kindStr = kind.toString();
  if (kindStr.includes('CALL')) {
    return t`Call`;
  }
  if (kindStr.includes('SMS')) {
    return t`SMS`;
  }
  if (kindStr.includes('EMAIL')) {
    return t`Email`;
  }
  if (kindStr.includes('CHAT')) {
    return t`Chat`;
  }
  return formatTaskKind(kind);
}

export function getTaskSubtitle(kind: SupportTaskKind, title?: string | null): string | null {
  return formatTaskTitle(kind, title);
}
